import React, { useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Alert,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { useTarget } from "../contexts/TargetContext";
import { useDialog } from "../contexts/DialogContext";
import EditForm from "./EditForm";

export const EditDialog: React.FC = () => {
  const {
    editDialogOpen,
    setEditDialogOpen,
    pendingEditTargetId,
    setPendingEditTargetId,
  } = useDialog();
  const { targets, selectedTarget, selectedNight, handleEdit } = useTarget();
  const prepareDataRef = useRef<() => any>(() => null);
  const [error, setError] = React.useState<string | null>(null);

  // Find the target to edit: use pendingEditTargetId if set, else selectedTarget
  const targetToEdit =
    (pendingEditTargetId
      ? targets.find((t) => t.id === pendingEditTargetId)
      : selectedTarget) || null;

  const handleSave = () => {
    if (targetToEdit) {
      try {
        const updatedData = prepareDataRef.current();
        handleEdit(updatedData);
        setEditDialogOpen(false);
        setPendingEditTargetId(null);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to save changes");
      }
    }
  };

  const handleClose = () => {
    setEditDialogOpen(false);
    setPendingEditTargetId(null);
  };

  if (!targetToEdit) return null;

  return (
    <Dialog
      open={editDialogOpen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 20px 40px rgba(0, 0, 0, 0.9)",
          backgroundColor: "#1a1a1a",
          border: "1px solid #333333",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: 28,
          pb: 2,
          backgroundColor: "#1a1a1a",
          color: "#ffffff",
          borderBottom: "1px solid #333333",
          background: "linear-gradient(135deg, #1a1a1a 0%, #252525 100%)",
        }}
      >
        Edit Target
      </DialogTitle>
      <DialogContent
        sx={{
          p: 4,
          pt: 5,
          backgroundColor: "#1a1a1a",
        }}
      >
        <Box sx={{ height: "100%" }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <EditForm
            selectedTarget={targetToEdit}
            selectedNight={selectedNight || "total"}
            onSave={handleEdit}
            prepareDataRef={prepareDataRef}
          />
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 4,
          pb: 4,
          pt: 3,
          backgroundColor: "#1a1a1a",
          justifyContent: "space-between",
          borderTop: "1px solid #333333",
        }}
      >
        <Button
          onClick={handleClose}
          sx={{
            color: "#a0a0a0",
            fontWeight: 500,
            fontSize: 16,
            px: 3,
            py: 1,
            "&:hover": {
              backgroundColor: "#2a2a2a",
              color: "#ffffff",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={<SaveIcon />}
          sx={{
            minWidth: 160,
            fontWeight: 600,
            fontSize: 16,
            px: 4,
            py: 1.5,
            backgroundColor: "#4caf50",
            color: "#fff",
            borderRadius: 2,
            textTransform: "none",
            boxShadow: "0 4px 12px rgba(76, 175, 80, 0.3)",
            "&:hover": {
              backgroundColor: "#66bb6a",
              boxShadow: "0 6px 16px rgba(76, 175, 80, 0.4)",
              transform: "translateY(-1px)",
            },
          }}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditDialog;
